// Pure logic of the scroll-scrubbed home hero, ported from the owner's reference page. No DOM here:
// the component and the player call these, and the spec next to it covers them.

export enum EHeroVariant {
    A = 'a',
    B = 'b',
    C = 'c',
    D = 'd',
}

/** What the hint at the bottom of the stage says */
export enum EHeroHint {
    /** Nothing is downloading, or everything is downloaded */
    Scroll = 'scroll',
    /** The first part is not ready yet: "Loading N%" */
    Loading = 'loading',
    /** The first part is ready, the rest is coming: "Scroll ↓ · N%" */
    ScrollLoading = 'scroll-loading',
}

export interface IHeroPart {
    name: string;
    frames: number;
}

/** Track height and framing differ by variant too; they live in the styles, keyed by `HERO_VARIANT_ATTR` */
export interface IHeroVariantMedia {
    parts: IHeroPart[];
    /** Card windows over the progress, `[from, to]` in 0..1 */
    stages: [number, number][];
}

export const HERO_FPS: number = 24;
export const HERO_STORAGE_KEY: string = 'gc_hero_variant';
/**
 * Attribute of `<html>` naming the shown variant. A script in the page head sets it before the first
 * paint, so only that variant's poster is fetched; the component sets it after a move inside the app
 */
export const HERO_VARIANT_ATTR: string = 'data-hero-variant';
/** Versioned folder in the public media bucket; a new upload goes to a new folder */
export const HERO_MEDIA_BASE: string = 'https://staircraft-media.fra1.cdn.digitaloceanspaces.com/hero/v1';
/** Posters lie in the site's own static files, next to the page: they are the largest paint */
export const HERO_POSTER_BASE: string = '/hero';
/** The header turns solid when the hero's bottom rises above this line, pixels from the window top */
export const HERO_HEADER_EDGE_PX: number = 80;
/** The intro block fades out and rises over this progress window */
export const HERO_INTRO_FADE: readonly [number, number] = [0.03, 0.09];
/** Past this progress the "scroll" hint is gone */
export const HERO_HINT_FADE: number = 0.03;
/** No byte for this long while the scroll is held: the hold is lifted for good */
export const HERO_STALL_MS: number = 20000;
/** Smoothing rate of the scrub: `pC += (pT - pC) * (1 - e^(-dt * rate))` */
export const HERO_EASE_RATE: number = 10;
export const HERO_FADE: number = 0.035;

export const HERO_VARIANTS: Readonly<Record<EHeroVariant, IHeroVariantMedia>> = {
    [EHeroVariant.A]: {
        parts: [
            { name: 'a1', frames: 118 },
            { name: 'a2', frames: 118 },
        ],
        stages: [
            [0.1, 0.4],
            [0.5, 0.7],
            [0.73, 0.88],
            [0.9, 1.01],
        ],
    },
    [EHeroVariant.B]: {
        parts: [
            { name: 'b1', frames: 100 },
            { name: 'b2', frames: 99 },
        ],
        stages: [
            [0.05, 0.24],
            [0.28, 0.46],
            [0.54, 0.76],
            [0.82, 1.01],
        ],
    },
    [EHeroVariant.C]: {
        parts: [
            { name: 'c1', frames: 118 },
            { name: 'c2', frames: 118 },
        ],
        stages: [
            [0.1, 0.4],
            [0.5, 0.7],
            [0.73, 0.88],
            [0.9, 1.01],
        ],
    },
    [EHeroVariant.D]: {
        parts: [
            { name: 'd1', frames: 100 },
            { name: 'd2', frames: 99 },
        ],
        stages: [
            [0.05, 0.24],
            [0.28, 0.46],
            [0.54, 0.76],
            [0.82, 1.01],
        ],
    },
};

const VARIANT_IDS: EHeroVariant[] = Object.values(EHeroVariant);

export function isHeroVariant(value: string | null | undefined): value is EHeroVariant {
    return VARIANT_IDS.some((variant: EHeroVariant) => variant === value);
}

/**
 * The variant to show and whether it must be stored. `#a`–`#d` in the address wins and is not stored;
 * a stored valid variant is kept; otherwise a random one is picked and stored.
 */
export function pickHeroVariant(hash: string, stored: string | null, random: number): { variant: EHeroVariant; store: boolean } {
    const fromHash: string = hash.replace(/^#/, '').toLowerCase();
    if (isHeroVariant(fromHash)) {
        return { variant: fromHash, store: false };
    }
    if (isHeroVariant(stored)) {
        return { variant: stored, store: false };
    }
    const index: number = Math.min(VARIANT_IDS.length - 1, Math.floor(random * VARIANT_IDS.length));
    return { variant: VARIANT_IDS[index], store: true };
}

/** 1920 parts only for a wide desktop screen; a phone or a narrow window gets 1280 */
export function heroVideoSize(isPhone: boolean, innerWidth: number, devicePixelRatio: number): 1280 | 1920 {
    return !isPhone && innerWidth * Math.min(devicePixelRatio || 1, 2) > 1500 ? 1920 : 1280;
}

export function heroPartUrl(base: string, part: string, size: number): string {
    const root: string = base.endsWith('/') ? base.slice(0, -1) : base;
    return `${root}/${part}_${String(size)}.mp4`;
}

/** Poster of a variant at a width; the head script builds the same address for its preload */
export function heroPosterUrl(variant: EHeroVariant, size: 1280 | 1920): string {
    return `${HERO_POSTER_BASE}/${variant}_poster_${String(size)}.webp`;
}

export function heroTotalFrames(parts: readonly IHeroPart[]): number {
    return parts.reduce((sum: number, part: IHeroPart) => sum + part.frames, 0);
}

export function clamp(value: number, min: number = 0, max: number = 1): number {
    return Math.min(max, Math.max(min, value));
}

export function smoothStep(from: number, to: number, x: number): number {
    const t: number = clamp((x - from) / (to - from));
    return t * t * (3 - 2 * t);
}

/** Opacity of the intro block: full at the top, gone past `HERO_INTRO_FADE` */
export function heroIntroShown(progress: number): number {
    return 1 - smoothStep(HERO_INTRO_FADE[0], HERO_INTRO_FADE[1], progress);
}

/** Opacity of a card shown over `[from, to]` of the progress, with soft edges */
export function heroFadeWindow(progress: number, from: number, to: number, fade: number = HERO_FADE): number {
    const fadeIn: number = from <= 0 ? 1 : smoothStep(from, from + fade, progress);
    const fadeOut: number = to >= 1 ? 0 : smoothStep(to - fade, to, progress);
    return Math.min(fadeIn, 1 - fadeOut);
}

/** Scroll progress of the hero track: 0 at its top, 1 when its bottom meets the window bottom */
export function heroProgress(trackTop: number, trackHeight: number, windowHeight: number): number {
    const travel: number = trackHeight - windowHeight;
    return travel > 0 ? clamp(-trackTop / travel) : 0;
}

/** Parts downloaded in a row from the start: the scroll cannot go past the first gap */
export function heroReadyPrefix(ready: readonly boolean[]): number {
    const gap: number = ready.indexOf(false);
    return gap === -1 ? ready.length : gap;
}

/** The largest progress the downloaded parts cover in a row: 0 without parts, 1 with all of them */
export function heroLoadedProgressMax(parts: readonly IHeroPart[], ready: readonly boolean[]): number {
    const prefix: number = heroReadyPrefix(ready);
    if (prefix === 0) {
        return 0;
    }
    const frames: number = heroTotalFrames(parts.slice(0, prefix));
    return clamp((frames - 1) / (heroTotalFrames(parts) - 1));
}

/** The page scroll, in pixels, that puts the hero track at `progressMax`; the inverse of `heroProgress` */
export function heroScrollCap(trackTopOnPage: number, trackHeight: number, windowHeight: number, progressMax: number): number {
    return Math.floor(trackTopOnPage + progressMax * Math.max(0, trackHeight - windowHeight));
}

/** One smoothing step from `current` towards `target`; reduced motion jumps straight there */
export function heroEase(current: number, target: number, dtSeconds: number, reducedMotion: boolean): number {
    const delta: number = target - current;
    if (reducedMotion || Math.abs(delta) < 1e-4) {
        return target;
    }
    return current + delta * (1 - Math.exp(-dtSeconds * HERO_EASE_RATE));
}

/**
 * The part and its time for a progress. A part not downloaded yet is not shown: the last frame of
 * the nearest earlier ready part is held instead. `null` — nothing is ready, the poster stays.
 */
export function heroFrameAt(progress: number, parts: IHeroPart[], ready: boolean[]): { part: number; time: number } | null {
    let frame: number = Math.round(clamp(progress) * (heroTotalFrames(parts) - 1));
    let part: number = 0;
    while (part < parts.length - 1 && frame >= parts[part].frames) {
        frame -= parts[part].frames;
        part++;
    }
    if (!ready[part]) {
        let earlier: number = part;
        while (earlier > 0 && !ready[earlier]) {
            earlier--;
        }
        if (!ready[earlier]) {
            return null;
        }
        part = earlier;
        frame = parts[earlier].frames - 1;
    }
    return { part, time: (frame + 0.5) / HERO_FPS };
}

/** The hint by the download state: `started` — the download began, `ready` — parts ready in order */
export function heroHint(started: boolean, ready: readonly boolean[]): EHeroHint {
    if (!started || ready.every((partReady: boolean) => partReady)) {
        return EHeroHint.Scroll;
    }
    return ready[0] ? EHeroHint.ScrollLoading : EHeroHint.Loading;
}

/** Share of the variant's bytes already received; an unknown size counts as 5 MB */
export function heroLoadPercent(received: number[], sizes: number[]): number {
    const total: number = sizes.reduce((sum: number, size: number) => sum + (size || 5e6), 0);
    const got: number = received.reduce((sum: number, bytes: number) => sum + bytes, 0);
    return total > 0 ? Math.round(clamp(got / total) * 100) : 0;
}

/**
 * Where one start-and-pause of a video part ended. Safari on an iPhone paints no frame for a seek
 * of a video that has never played, so every opened part is started once and paused at once
 */
export enum EHeroPrime {
    /** The video played: seeks paint frames from now on */
    Primed = 'primed',
    /** The browser wants a gesture first (Low Power Mode, or a play cut short): tried again on the next tap */
    Gesture = 'gesture',
    /** The video cannot play at all: not tried again */
    Failed = 'failed',
}

/** The outcome of `video.play()` by the name of its error; `null` — it played */
export function heroPrimeOutcome(errorName: string | null): EHeroPrime {
    if (errorName === null) {
        return EHeroPrime.Primed;
    }
    return errorName === 'NotAllowedError' || errorName === 'AbortError' ? EHeroPrime.Gesture : EHeroPrime.Failed;
}

/**
 * A downloaded part whose video has not learned its frames this long holds the page no longer: Safari
 * on an iPhone may wait for a gesture before it reads the media. The poster stays, the scroll is free
 */
export const HERO_OPEN_STALL_MS: number = 5000;

/**
 * The scroll hold is lifted for good: no bytes for `HERO_STALL_MS`, or a downloaded part waits for its
 * frames longer than `HERO_OPEN_STALL_MS`. `openWaitSince` is `-1` while no part waits
 */
export function heroHoldStalled(now: number, lastBytesAt: number, openWaitSince: number): boolean {
    if (openWaitSince >= 0 && now - openWaitSince > HERO_OPEN_STALL_MS) {
        return true;
    }
    return now - lastBytesAt > HERO_STALL_MS;
}

/** Parts to start now: given their Blob address, not settled (primed or failed), not being started already */
export function heroPartsToPrime(sourced: readonly boolean[], settled: readonly boolean[], priming: readonly boolean[]): number[] {
    const parts: number[] = [];
    sourced.forEach((isSourced: boolean, part: number): void => {
        if (isSourced && !settled[part] && !priming[part]) {
            parts.push(part);
        }
    });
    return parts;
}

/** Every part is settled: the gesture listeners have nothing left to do */
export function heroPrimeComplete(settled: readonly boolean[]): boolean {
    return settled.every((isSettled: boolean): boolean => isSettled);
}
